import React, { useState, useEffect, useCallback } from "react";
import DataTable from "../../../Pages/InputField/DataTable";
import { Modal, Button, Table, Row, Col } from "react-bootstrap";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import baseURL from "../../../../Url/NodeBaseURL";
import { BlobProvider } from "@react-pdf/renderer";
import EstimateA4 from "./EstimateA4";

const RepairsTable = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]); // table rows
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null); // data for modal
  const [showModal, setShowModal] = useState(false);
  const [directPrintData, setDirectPrintData] = useState(null); // data for direct printing
  const [urdPurchaseData, setUrdPurchaseData] = useState([]); // URD purchase data for modal
  const [urdPurchaseAmount, setUrdPurchaseAmount] = useState(0); // Total URD purchase amount
  const [urdDataReady, setUrdDataReady] = useState(false); // Track when URD data is ready for PDF
  const [salesReturnData, setSalesReturnData] = useState([]); // Sales return data for modal
  const [salesReturnAmount, setSalesReturnAmount] = useState(0); // Total sales return amount
  const [viewLoading, setViewLoading] = useState(null); // Loading state for view button - stores estimate number being loaded
  const [overlayLoading, setOverlayLoading] = useState(false); // Overlay loading state for PDF generation

  /* ---------------------------- fetch summary ---------------------------- */

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${baseURL}/Estimate/summary`);
      console.log("Raw estimate summary data:", res.data.summary);
      
      // Get date 24 hours ago
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);

      const tableRows = res.data.summary
        .filter((est) => {
          const estimateDate = new Date(est.uniqueData.created_at);
          // Show estimates from last 24 hours
          return estimateDate >= twentyFourHoursAgo;
        })
        .map((est) => {
          // Log each estimate number to debug
          console.log("Processing estimate:", est.uniqueData.estimate_number);
          
          return {
            date: est.uniqueData.created_at,
            estimate_number: est.uniqueData.estimate_number,
            product_names: est.repeatedData.map((p) => p.product_name).join(", "),
            net_amount: est.uniqueData.net_amount,
          };
        })
        .sort((a, b) => {
          // Sort by date first (newest first), then by estimate number
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          if (dateA.getTime() !== dateB.getTime()) {
            return dateB.getTime() - dateA.getTime();
          }
          // If same date, sort by estimate number (newest first)
          const aNum = parseInt(a.estimate_number.replace(/[^\d]/g, ""), 10);
          const bNum = parseInt(b.estimate_number.replace(/[^\d]/g, ""), 10);
          return bNum - aNum;
        });

      setRows(tableRows);
    } catch (err) {
      console.error("Error fetching summary:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  /* call it once on mount */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Debug useEffect to monitor URD data changes
  useEffect(() => {
    console.log("URD Data State Changed:");
    console.log("URD Purchase Data:", urdPurchaseData);
    console.log("URD Purchase Amount:", urdPurchaseAmount);
    console.log("URD Data Ready:", urdDataReady);
  }, [urdPurchaseData, urdPurchaseAmount, urdDataReady]);

  /* ---------------------------- fetch URD purchase data ---------------------------- */
  const fetchUrdPurchaseData = async (customerMobile, customerName, estimateNumber = null) => {
    try {
      console.log("Fetching URD purchase data for:", { customerMobile, customerName, estimateNumber });
      
      // Use the same API as the estimate edit screen
      if (estimateNumber) {
        console.log("Fetching URD purchase data by estimate number:", estimateNumber);
        const response = await axios.get(`${baseURL}/get/olditems/${estimateNumber}`);
        console.log("URD purchase API response:", response.data);
        
        if (response.data && Array.isArray(response.data)) {
          const urdPurchases = response.data;
          const totalAmount = urdPurchases.reduce((sum, item) => {
            const amount = parseFloat(item.total_amount || 0);
            console.log(`URD Purchase item: ${amount}`);
            return sum + amount;
          }, 0);
          
          console.log("Total URD purchase amount calculated:", totalAmount);
          
          setUrdPurchaseData(urdPurchases);
          setUrdPurchaseAmount(totalAmount);
          setUrdDataReady(true);
          
          return { purchases: urdPurchases, totalAmount };
        } else {
          console.log("No URD purchase data found for estimate:", estimateNumber);
          setUrdPurchaseData([]);
          setUrdPurchaseAmount(0);
          setUrdDataReady(true);
          return { purchases: [], totalAmount: 0 };
        }
      } else {
        console.log("No estimate number provided, cannot fetch URD purchase data");
        setUrdPurchaseData([]);
        setUrdPurchaseAmount(0);
        setUrdDataReady(true);
        return { purchases: [], totalAmount: 0 };
      }
    } catch (error) {
      console.error("Error fetching URD purchase data:", error);
      setUrdPurchaseData([]);
      setUrdPurchaseAmount(0);
      setUrdDataReady(true);
      return { purchases: [], totalAmount: 0 };
    }
  };

  /* ---------------------------- fetch sales return data ---------------------------- */
  const fetchSalesReturnData = async (estimateNumber) => {
    try {
      console.log("Fetching sales return data for estimate:", estimateNumber);
      
      if (!estimateNumber) {
        console.log("No estimate number provided, cannot fetch sales return data");
        setSalesReturnData([]);
        setSalesReturnAmount(0);
        return;
      }

      // Use the same API as the estimate edit screen
      const response = await axios.get(`${baseURL}/get-estimate-sale-returns/${estimateNumber}`);
      console.log("Sales return API response:", response.data);
      
      if (response.data && Array.isArray(response.data)) {
        const salesReturns = response.data;
        const totalAmount = salesReturns.reduce((sum, item) => {
          const amount = parseFloat(item.total_price || 0);
          console.log(`Sales Return item: ${amount}`);
          return sum + amount;
        }, 0);
        
        console.log("Total sales return amount calculated:", totalAmount);
        
        setSalesReturnData(salesReturns);
        setSalesReturnAmount(totalAmount);
      } else {
        console.log("No sales return data found for estimate:", estimateNumber);
        setSalesReturnData([]);
        setSalesReturnAmount(0);
      }
    } catch (error) {
      console.error("Error fetching sales return data:", error);
      setSalesReturnData([]);
      setSalesReturnAmount(0);
    }
  };

  const formatDate = (date) => {
    if (!date) return ""; // Return an empty string if no date is provided

    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0"); // Pad single digit days with 0
    const month = String(d.getMonth() + 1).padStart(2, "0"); // Pad months with 0
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
  };

  /* ------------------------ column definitions --------------------------- */
  const columns = React.useMemo(
    () => [
      {
        Header: "Sr. No.",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Date",
        accessor: "date",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("en-GB") : "",
      },
      { Header: "Estimate #", accessor: "estimate_number" },
      { Header: "Product Names", accessor: "product_names" },
      { Header: "Net Amount", accessor: "net_amount" },
      {
        Header: "Estimate",
        Cell: ({ row }) => {
          const estimateNumber = row.original?.estimate_number;

          // Show 'N/A' estimate number is missing
          if (!estimateNumber) {
            return <span style={{ color: "gray" }}>N/A</span>;
          }

          return (
            
            <span
              style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }}
              onClick={() => handleDirectPrint(row.original.estimate_number)}
            >
              📝 View
            </span>
          );
        },
      },

      {
        Header: "Actions",
        Cell: ({ row }) => {
          const isThisRowLoading = viewLoading === row.original.estimate_number;
          return (
            <div style={{ display: "flex", gap: 10 }}>
              {isThisRowLoading ? (
                <div style={{ color: "gray", cursor: "not-allowed" }}>
                  <div className="spinner-border spinner-border-sm" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <FaEye
                  style={{ cursor: "pointer", color: "green" }}
                  onClick={() => handleView(row.original.estimate_number)}
                />
              )}
              <FaEdit
                style={{ cursor: "pointer", color: "blue" }}
                onClick={() => handleEdit(row.original.estimate_number)}
              />
              <FaTrash
                style={{ cursor: "pointer", color: "red" }}
                onClick={() => handleDelete(row.original.estimate_number)}
              />
            </div>
          );
        },
      },
    ],
    []
  );

  const handleEdit = async (estimate_number) => {
    console.log("Attempting to edit estimate with number:", estimate_number);
    
    // Check if the estimate number starts with INV instead of EST
    if (estimate_number && estimate_number.startsWith('INV')) {
      console.warn("Warning: Estimate number starts with 'INV' instead of 'EST':", estimate_number);
      // You might want to show a warning to the user here
    }
    
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to edit this record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, go ahead!",
      cancelButtonText: "No, cancel",
    });

    if (result.isConfirmed) {
      try {
        console.log("Making API call to:", `${baseURL}/Estimate/full-details/${estimate_number}`);
        const response = await axios.get(`${baseURL}/Estimate/full-details/${estimate_number}`);
        const data = response.data;
        console.log("Full estimate data:", data);
        
        if (!data || !data.estimate) {
          console.error("No estimate data found in response:", data);
          Swal.fire(
            "Error",
            "No estimate details found for the provided estimate number.",
            "error"
          );
          return;
        }

        navigate("/estimates", {
          state: { estimateData: data }
        });
      } catch (error) {
        console.error("Error fetching estimate details:", error);
        Swal.fire(
          "Error",
          "Unable to fetch estimate details. Please try again.",
          "error"
        );
      }
    } else {
      Swal.fire("Cancelled", "Edit operation was cancelled.", "info");
    }
  };

  const handleDelete = async (estimateNumber) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Do you really want to delete order ${estimateNumber}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await fetch(
            `${baseURL}/Estimate/${estimateNumber}`,
            {
              method: "DELETE",
            }
          );

          if (!response.ok) {
            throw new Error("Failed to delete estimate");
          }
          fetchData();
          Swal.fire("Deleted!", "The estimate has been deleted.", "success");
        } catch (error) {
          console.error("Error deleting estimate:", error.message);
          Swal.fire("Error!", "Failed to delete the estimate.", "error");
        }
      }
    });
  };

  const handleView = async (estNo) => {
    setViewLoading(estNo);
    try {
      const res = await axios.get(`${baseURL}/Estimate/summary`);
      const allEstimates = res.data.summary;

      const matchedEstimate = allEstimates.find(
        (est) => est.uniqueData.estimate_number === estNo
      );

      if (!matchedEstimate) {
        Swal.fire("Not Found", `Estimate ${estNo} not found.`, "warning");
        return;
      }

      // Fetch URD purchase and sales return data for this estimate
      const estimateNumber = matchedEstimate.uniqueData.estimate_number || "";
      
      // Fetch URD purchase data using the same API as edit screen
      await fetchUrdPurchaseData("", "", estimateNumber);
      
      // Fetch sales return data using the same API as edit screen
      await fetchSalesReturnData(estimateNumber);

      setDetail(matchedEstimate);
      setShowModal(true);
    } catch (err) {
      console.error("Error loading estimate summary:", err);
      Swal.fire("Error", "Could not load estimate details.", "error");
    } finally {
      setViewLoading(null);
    }
  };

  const handleDirectPrint = async (estNo) => {
    setOverlayLoading(true);
    try {
      console.log("handleDirectPrint called with:", estNo);
      
      // Reset URD data ready state
      setUrdDataReady(false);
      
      const res = await axios.get(`${baseURL}/Estimate/summary`);
      const allEstimates = res.data.summary;

      const matchedEstimate = allEstimates.find(
        (est) => est.uniqueData.estimate_number === estNo
      );

      if (!matchedEstimate) {
        Swal.fire("Not Found", `Estimate ${estNo} not found.`, "warning");
        return;
      }

      console.log("Found estimate:", matchedEstimate);

      // Fetch URD purchase and sales return data for this estimate before generating PDF
      const estimateNumber = matchedEstimate.uniqueData.estimate_number || "";
      
      console.log("Fetching data for estimate:", estimateNumber);
      
      // Fetch URD purchase data using the same API as edit screen
      await fetchUrdPurchaseData("", "", estimateNumber);
      console.log("URD purchase data fetched. Amount:", urdPurchaseAmount);
      
      // Fetch sales return data using the same API as edit screen
      await fetchSalesReturnData(estimateNumber);
      console.log("Sales return data fetched. Amount:", salesReturnAmount);

      // Set the direct print data for PDF generation
      setDirectPrintData(matchedEstimate);

      // Wait for URD data to be ready, then trigger PDF generation
      const waitForUrdData = () => {
        if (urdDataReady) {
          console.log("URD data is ready, triggering PDF generation");
          const directPrintButton = document.querySelector('#direct-print-pdf');
          if (directPrintButton) {
            console.log("Found direct print button, clicking it");
            directPrintButton.click();
          } else {
            console.error("Direct print button not found");
          }
        } else {
          console.log("Waiting for URD data to be ready...");
          // Add a timeout to prevent infinite waiting
          if (Date.now() - startTime > 5000) { // 5 second timeout
            console.log("Timeout reached, proceeding with PDF generation without URD data");
            const directPrintButton = document.querySelector('#direct-print-pdf');
            if (directPrintButton) {
              directPrintButton.click();
            }
            return;
          }
          setTimeout(waitForUrdData, 100);
        }
      };

      // Start waiting for URD data with timeout
      const startTime = Date.now();
      setTimeout(waitForUrdData, 100);

    } catch (err) {
      console.error("Error in handleDirectPrint:", err);
      Swal.fire("Error", "Could not generate PDF.", "error");
      setOverlayLoading(false);
    }
  };

  // Close the modal
  const closeModal = () => {
    setShowModal(false);
    setDetail(null);
    setUrdPurchaseData([]);
    setUrdPurchaseAmount(0);
    setUrdDataReady(false); // Reset URD data ready state
    setSalesReturnData([]);
    setSalesReturnAmount(0);
  };

  const createNew = () => {
    navigate("/estimates/v2"); // Navigate to the /suppliers page
  };

  const fmt = (d) => (!d ? "" : new Date(d).toLocaleDateString("en-GB"));

  /* ------------------------------- render ------------------------------- */
  return (
    <div className="main-container">
      {/* Overlay Loader */}
      {overlayLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              padding: "30px",
              borderRadius: "10px",
              textAlign: "center",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}>
              Generating PDF...
            </div>
            <div style={{ fontSize: "14px", color: "#666", marginTop: "10px" }}>
              Please wait while we prepare your estimate
            </div>
          </div>
        </div>
      )}
      <div className="sales-table-container">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h3>Estimates</h3>
            <Button
              className="create_but"
              onClick={createNew}
              style={{ backgroundColor: "#a36e29", borderColor: "#a36e29" }}
            >
              + Create
            </Button>
          </Col>
        </Row>

        {loading ? (
          <p>Loading…</p>
        ) : (
          <DataTable columns={columns} data={rows} />
        )}
      </div>

      {/* Direct PDF generation for "View" links */}
      {directPrintData && (
        <BlobProvider
          document={
            <EstimateA4
              formData={{
                estimate: directPrintData.uniqueData.estimate_number || "N/A",
                date: directPrintData.uniqueData.date || new Date().toISOString(),
                time: new Date().toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                customerName: directPrintData.uniqueData.account_name || "Customer",
                mobile: directPrintData.uniqueData.mobile || "N/A",
              }}
              items={directPrintData.repeatedData.map((item) => ({
                categoty: `${item.category || 'General'}`,
                item: `${item.product_name || 'Product'} (${item.metal || item.metal_type || 'N/A'})`,
                rate: item.rate || 0,
                weight: item.total_wt || item.total_weight_av
                  ? `${item.total_wt || item.total_weight_av} GM`
                  : "0 GM",
                mc: `${item.mc || item.making_charges || 0}`,
                value: item.total_price || 0,
                discount: item.discount || 0,
                remarks: item.remarks || "",
              }))}
              subtotal={directPrintData.uniqueData.net_amount || 0}
              cgst={directPrintData.uniqueData.cgst || 0}
              sgst={directPrintData.uniqueData.sgst || 0}
              grandTotal={directPrintData.uniqueData.net_amount || 0}
              urdPurchaseData={urdPurchaseData}
              urdPurchaseAmount={urdPurchaseAmount}
              salesReturnData={salesReturnData}
              salesReturnAmount={salesReturnAmount}
            />
          }
        >
        {({ blob, loading, error }) => {
          const handleDirectPrintPDF = () => {
            if (blob) {
              const blobUrl = URL.createObjectURL(blob);
              const newWindow = window.open(blobUrl, "_blank");

              if (newWindow) {
                // Wait for the PDF to load before triggering print
                newWindow.onload = () => {
                  newWindow.focus();
                  newWindow.print();
                  setOverlayLoading(false); // Hide overlay when PDF opens
                };
                // Fallback: Use setTimeout in case onload doesn't fire
                setTimeout(() => {
                  setOverlayLoading(false); // Hide overlay after 2 seconds as fallback
                }, 2000);
              } else {
                console.error("Failed to open new window. Please allow pop-ups.");
                alert("Please allow pop-ups to view and print the PDF.");
                setOverlayLoading(false); // Hide overlay if popup blocked
              }
              
              // Clear the direct print data after generating PDF
              setTimeout(() => {
                setDirectPrintData(null);
                setUrdDataReady(false);
              }, 1000);
            } else {
              console.error("Blob is not available.");
              alert("Failed to generate PDF. Please try again.");
            }
          };

          return (
            <button
              id="direct-print-pdf"
              className="btn btn-primary d-none"
              onClick={handleDirectPrintPDF}
              disabled={loading || error}
            >
              {loading ? "Generating..." : "Direct Print PDF"}
            </button>
          );
        }}
        </BlobProvider>
      )}

      {/* ---------------------------- detail modal --------------------------- */}
      <Modal show={showModal} onHide={closeModal} size="xl" className="m-auto">
        <Modal.Header closeButton>
          <Modal.Title>Estimate Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {detail && detail.uniqueData && detail.repeatedData ? (
            <>
              <Table bordered>
                <tbody>
                  <tr>
                    <td>Date</td>
                    <td>{fmt(detail.uniqueData.date)}</td>
                  </tr>
                  <tr>
                    <td>Estimate #</td>
                    <td>{detail.uniqueData.estimate_number}</td>
                  </tr>
                  <tr>
                    <td>Customer</td>
                    <td>
                      {detail.uniqueData.account_name || "N/A"} 
                      {detail.uniqueData.mobile && ` (${detail.uniqueData.mobile})`}
                    </td>
                  </tr>
                  <tr>
                    <td>Net Amount</td>
                    <td>{detail.uniqueData.net_amount}</td>
                  </tr>
                </tbody>
              </Table>

              <h5>Products</h5>
              <div className="table-responsive">
                <Table bordered>
                  <thead>
                    <tr>
                      <th>Barcode</th>
                      <th>Product</th>
                      <th>Metal</th>
                      <th>Purity</th>
                      <th>Gr Wt</th>
                      <th>St Wt</th>
                      <th>Total Wt</th>
                      <th>Rate</th>
                      <th>MC</th>
                      <th>Discount</th>
                      <th>Tax Amt</th>
                      <th>Total Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.repeatedData.map((p, i) => (
                      <tr key={i}>
                        <td>{p.barcode}</td>
                        <td>{p.product_name}</td>
                        <td>{p.metal}</td>
                        <td>{p.purity}</td>
                        <td>{p.gr_wt}</td>
                        <td>{p.st_wt}</td>
                        <td>{p.total_wt}</td>
                        <td>{p.rate}</td>
                        <td>{p.mc}</td>
                        <td>{p.discount ?? "-"}</td>
                        <td>{p.tax_amt}</td>
                        <td>{p.total_price}</td>
                      </tr>
                    ))}
                    
                    {/* URD Purchase Row */}
                    <tr style={{ backgroundColor: "#f8f9fa", fontWeight: "bold" }}>
                      <td colSpan={11} className="text-end">
                        URD Purchase Amount
                      </td>
                      <td style={{ color: "red" }}>
                        -{urdPurchaseAmount.toFixed(2)}
                      </td>
                    </tr>
                    
                    {/* Final Net Amount Row */}
                    <tr style={{ fontWeight: "bold", backgroundColor: "#e9ecef" }}>
                      <td colSpan={11} className="text-end">
                        Final Net Amount
                      </td>
                      <td>
                        {(parseFloat(detail.uniqueData.net_amount) - urdPurchaseAmount).toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {/* URD Purchase Details Section */}
              {urdPurchaseData.length > 0 && (
                <>
                  <h5 style={{ marginTop: "20px" }}>URD Purchase Details</h5>
                  <div className="table-responsive">
                    <Table bordered striped>
                      <thead>
                        <tr>
                          <th>URD Purchase #</th>
                          <th>Product</th>
                          <th>Metal</th>
                          <th>Purity</th>
                          <th>Gross</th>
                          <th>Net Wt</th>
                          <th>Rate</th>
                          <th>Amount</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {urdPurchaseData.map((purchase, i) => (
                          <tr key={i}>
                            <td>{purchase.urdpurchase_number}</td>
                            <td>{purchase.product_name}</td>
                            <td>{purchase.metal}</td>
                            <td>{purchase.purity}</td>
                            <td>{purchase.gross}</td>
                            <td>{purchase.eqt_wt}</td>
                            <td>{purchase.rate}</td>
                            <td>{parseFloat(purchase.total_amount).toFixed(2)}</td>
                            <td>{formatDate(purchase.date)}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: "bold", backgroundColor: "#f8f9fa" }}>
                          <td colSpan={7} className="text-end">
                            Total URD Purchase Amount
                          </td>
                          <td>{urdPurchaseAmount.toFixed(2)}</td>
                          <td></td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
              
              {/* Show message when no customer data is available */}
              {(!detail.uniqueData.account_name && !detail.uniqueData.mobile) && (
                <div style={{ marginTop: "20px", padding: "10px", backgroundColor: "#f8f9fa", borderRadius: "5px" }}>
                  <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
                    <strong>Note:</strong> Customer information is not available for this estimate. 
                    URD purchase data cannot be displayed without customer details.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p>Loading details or invalid estimate data.</p>
          )}
        </Modal.Body>

        <Modal.Footer>
          {detail && detail.uniqueData && detail.repeatedData && (
            <BlobProvider
              document={
                <EstimateA4
                  formData={{
                    estimate: detail.uniqueData.estimate_number || "N/A",
                    date: detail.uniqueData.date || new Date().toISOString(),
                    time: new Date().toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                    customerName: detail.uniqueData.account_name || "Customer",
                    mobile: detail.uniqueData.mobile || "N/A",
                  }}
                  items={detail.repeatedData.map((item) => ({
                    categoty: `${item.category || 'General'}`,
                    item: `${item.product_name || 'Product'} (${item.metal || item.metal_type || 'N/A'})`,
                    rate: item.rate || 0,
                    weight: item.total_wt || item.total_weight_av
                      ? `${item.total_wt || item.total_weight_av} GM`
                      : "0 GM",
                    mc: `${item.mc || item.making_charges || 0}`,
                    value: item.total_price || 0,
                    discount: item.discount || 0,
                    remarks: item.remarks || "",
                  }))}
                  subtotal={detail.uniqueData.net_amount || 0}
                  cgst={detail.uniqueData.cgst || 0}
                  sgst={detail.uniqueData.sgst || 0}
                  grandTotal={detail.uniqueData.net_amount || 0}
                  urdPurchaseData={urdPurchaseData}
                  urdPurchaseAmount={urdPurchaseAmount}
                  salesReturnData={[]}
                  salesReturnAmount={0}
                />
              }
            >
              {({ blob, loading, error }) => {
                const handlePrintPDF = () => {
                  if (blob) {
                    const blobUrl = URL.createObjectURL(blob);
                    const newWindow = window.open(blobUrl, "_blank");

                    if (newWindow) {
                      // Wait for the PDF to load before triggering print
                      newWindow.onload = () => {
                        newWindow.focus(); // Ensure the new window is focused
                        newWindow.print(); // Trigger the print dialog
                      };
                      // Fallback: Use setTimeout in case onload doesn't fire
                      // setTimeout(() => {
                      //   newWindow.focus();
                      //   newWindow.print();
                      // }, 2000); // 2-second delay as a fallback
                    } else {
                      console.error(
                        "Failed to open new window. Please allow pop-ups."
                      );
                      alert("Please allow pop-ups to view and print the PDF.");
                    }
                  } else {
                    console.error("Blob is not available.");
                    alert("Failed to generate PDF. Please try again.");
                  }
                };

                return (
                  <Button
                    variant="primary"
                    onClick={handlePrintPDF}
                    disabled={loading || error}
                    style={{ marginRight: "10px" }}
                  >
                    {loading ? "Generating PDF..." : "Print PDF"}
                  </Button>
                );
              }}
            </BlobProvider>
          )}
          <Button variant="secondary" onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
export default RepairsTable;
